import { usesPerformanceLabNavbar } from 'components/performance-lab/performanceLabNavRoutes';
import { MONSOON_OFFER_ENDS } from 'components/performance-lab/buy/india/planOffer';
import { LAB_PRICE_CUT_ENDS } from 'components/performance-lab/buy/india/priceCut';
import type { BannerCampaign, BannerContext } from './types';
import {
  EXCLUDED_REGIONS,
  isExcludedRegion,
  isRingAirUnavailable,
} from './regions';

/** Festive Lab promo on the India strip (matches Lab EXINPL15 end). */
const FESTIVE_BANNER_FROM = '2026-10-23T00:00:00+05:30';
const FESTIVE_BANNER_UNTIL = LAB_PRICE_CUT_ENDS;

/** India during the festive window: the Lab gift line runs without Pulsomics. */
const isIndiaFestive = ({ region, now }: BannerContext) =>
  region === 'IN' &&
  now >= Date.parse(FESTIVE_BANNER_FROM) &&
  now < Date.parse(FESTIVE_BANNER_UNTIL);

/** Master switch. */
export const BANNER_FEATURE_ACTIVE = true;

/** Routes that fall back to the market default even on /buy. */
const DEFAULT_OVERRIDE_ROUTES = new Set([
  '/ring',
  '/ring/buy',
  '/diesel-ultrahuman-ring',
  '/diesel-ultrahuman-ring/buy',
  '/rare',
  '/rare/buy',
]);

const isBuyOrPricing = (p: string) =>
  p.endsWith('/buy') || p.endsWith('/pricing');

/** Ring-AIR-unavailable geos keep the market default on these buy routes. */
const showsDefault = (ctx: BannerContext) =>
  !isBuyOrPricing(ctx.path) ||
  (DEFAULT_OVERRIDE_ROUTES.has(ctx.path) && isRingAirUnavailable(ctx.region));

/** Whole-page suppressions, checked before any campaign. */
export const SUPPRESSED: ((ctx: BannerContext) => boolean)[] = [
  () => !BANNER_FEATURE_ACTIVE,
  // Buy and the gift flow keep their own chrome (the strip links to the gift
  // page). The other Lab navbar pages (diagnostics, recovery, IV, GLP, contact)
  // show the strip only in India during the festive window, as on main otherwise.
  (ctx) =>
    ctx.path === '/performance-lab/buy' ||
    ctx.path === '/performance-lab/gift' ||
    ctx.path === '/performance-lab/gift/checkout' ||
    ctx.path.startsWith('/performance-lab/gift/checkout/') ||
    (usesPerformanceLabNavbar(ctx.path) && !isIndiaFestive(ctx)),
  // Conditional, matching main: the DEFAULT_OVERRIDE_ROUTES branch returns the
  // market default BEFORE main's `/rare` suppression, so ring-AIR-unavailable
  // geos keep their strip on /rare. Suppressing unconditionally dropped it.
  ({ path, region }) => path === '/rare' && !isRingAirUnavailable(region),
  ({ region }) => !region || region === 'XX',
];

export const BANNER_CAMPAIGNS: BannerCampaign[] = [
  // Pulsomics launch. NO time window: main gates this on a bare
  // `PULSOMICS_CAMPAIGN_ACTIVE = true` with the comment "Flip to false to end
  // the campaign" and ships no date at all, so inventing one here would
  // silently retire a campaign main currently runs. Deleting this row is the
  // equivalent switch. Priority 15 = ahead of the region tiles (20), behind
  // shipping (10), which it can never co-resolve with anyway.
  // `|| isExcludedRegion` matches main: an excluded region on a buy/pricing
  // route outside DEFAULT_OVERRIDE_ROUTES falls through to the market default
  // there, and so still sees this tile.
  {
    id: 'pulsomics',
    priority: 15,
    when: (ctx) =>
      (showsDefault(ctx) || isExcludedRegion(ctx.region)) &&
      !isIndiaFestive(ctx),
  },
  {
    id: 'shipping',
    priority: 10,
    exceptRegions: EXCLUDED_REGIONS,
    when: (ctx) => !showsDefault(ctx),
  },
  {
    id: 'm2-live',
    priority: 20,
    regions: ['US'],
    when: showsDefault,
  },
  {
    id: 'performance-lab-monsoon',
    priority: 20,
    regions: ['IN'],
    until: MONSOON_OFFER_ENDS,
    when: showsDefault,
  },
  // Festive Lab 15% — India strip through Nov 10, the "Gift health forward" line
  // only. Takes the IN default slot ahead of SDD while the window is open. The
  // "Diwali Exclusive" tile (performance-lab-diwali) stays in TILES unused.
  {
    id: 'performance-lab-gift',
    priority: 20,
    regions: ['IN'],
    from: FESTIVE_BANNER_FROM,
    until: FESTIVE_BANNER_UNTIL,
    when: showsDefault,
  },
  // SDD 2-hour launch takes the IN default strip. main did this by repointing
  // `config.bannerComponent` from PerformanceLabBanner to TwoHourDeliveryBanner
  // and leaving the old component defined-but-unused behind an eslint-disable;
  // here the rotation is data, so `performance-lab-glp` simply keeps its tile
  // in TILES with no campaign entry and can be re-enabled by re-adding one.
  // Resumes after the festive window so the two do not rotate together.
  {
    id: 'sdd-two-hour',
    priority: 20,
    regions: ['IN'],
    from: FESTIVE_BANNER_UNTIL,
    when: showsDefault,
  },
  // UAE 4-hour delivery launch, cloned from sdd-two-hour. AE only (not
  // QA/BH/KW/OM). No end date, same reasoning as sdd-two-hour above.
  {
    id: 'sdd-four-hour-ae',
    priority: 20,
    regions: ['AE'],
    when: showsDefault,
  },
];
